import React, { Suspense, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Skeleton,
} from '@mui/material';
import {
  DeviceOverview,
  DeviceCommands,
  DeviceUpdateHistory,
} from '@components/device-details';
import { Terminal } from '@components/Terminal';
import { LoadingWrapper } from '@components/common/LoadingWrapper';
import { RetryError } from '@components/common/RetryError';
import ErrorBoundary from '@components/error/ErrorBoundary';
import { useGetDeviceQuery } from '@store/api/deviceApi';
import { useDeviceTabState, useLazyTabContent } from '@hooks/useDeviceTabState';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = memo(({ children, value, index, ...other }) => {
  const { shouldRender, isActive } = useLazyTabContent(value, index);

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`device-tabpanel-${index}`}
      aria-labelledby={`device-tab-${index}`}
      {...other}
    >
      {shouldRender && (
        <Box sx={{ py: 3, display: isActive ? 'block' : 'none' }}>
          {children}
        </Box>
      )}
    </div>
  );
});

TabPanel.displayName = 'TabPanel';

const a11yProps = (index: number) => {
  return {
    id: `device-tab-${index}`,
    'aria-controls': `device-tabpanel-${index}`,
  };
};

const DeviceDetailsPage: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();

  // Use custom hook for tab state management with persistence
  const { tabValue, handleTabChange } = useDeviceTabState({
    deviceId: deviceId || '',
    defaultTab: 0,
  });

  // Fetch device data using RTK Query
  const { data: device, isLoading, error, refetch } = useGetDeviceQuery(deviceId || '', {
    skip: !deviceId,
  });

  const deviceName = device?.name || `Device ${deviceId}`;

  // Handle loading state
  if (isLoading) {
    return (
      <Container maxWidth={false}>
        <LoadingWrapper loading={true} skeleton="custom" customSkeleton={
          <Box>
            <Skeleton variant="text" width={300} height={32} sx={{ mb: 3 }} />
            <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
            <Paper sx={{ width: '100%' }}>
              <Skeleton variant="rectangular" height={48} />
              <Box sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={400} />
              </Box>
            </Paper>
          </Box>
        }>
          {null}
        </LoadingWrapper>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth={false}>
        <RetryError
          error={error as Error}
          onRetry={async () => {
            await refetch();
          }}
          title="Failed to load device details"
        />
      </Container>
    );
  }

  // Handle no device found
  if (!device) {
    return (
      <Container maxWidth={false}>
        <RetryError
          error="Device not found"
          onRetry={async () => {
            navigate('/devices');
          }}
          title="Device not found"
        />
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container maxWidth={false}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          {deviceName}
        </Typography>
      </Box>

      {/* Tabs Container */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="device details tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="Commands" {...a11yProps(1)} />
            <Tab label="Update History" {...a11yProps(2)} />
            <Tab label="Terminal" {...a11yProps(3)} />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={tabValue} index={0}>
            <Suspense fallback={<LoadingWrapper loading={true} skeleton="card">{null}</LoadingWrapper>}>
              <DeviceOverview deviceId={deviceId || ''} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Suspense fallback={<LoadingWrapper loading={true} skeleton="table">{null}</LoadingWrapper>}>
              <DeviceCommands deviceId={deviceId || ''} device={device} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Suspense fallback={<LoadingWrapper loading={true} skeleton="table">{null}</LoadingWrapper>}>
              <DeviceUpdateHistory deviceId={deviceId || ''} />
            </Suspense>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Suspense fallback={<LoadingWrapper loading={true} skeleton="custom" customSkeleton={
              <Box>
                <Skeleton variant="rectangular" height={500} />
              </Box>
            }>{null}</LoadingWrapper>}>
              <Terminal deviceId={deviceId || ''} deviceName={deviceName} />
            </Suspense>
          </TabPanel>
        </Box>
      </Paper>
    </Container>
    </ErrorBoundary>
  );
};

export default DeviceDetailsPage;
